import { useState } from 'react';
import { Search, Menu, X } from 'lucide-react';
import { useRouter } from '../router/MarketplaceRouter';

export function MarketplaceNav() {
  const { route, navigate } = useRouter();
  const [open, setOpen] = useState(false);
  const transparent = route.name === 'home';

  const linkStyle: React.CSSProperties = {
    fontFamily: 'var(--font-family-body)',
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    padding: '0.5rem 0',
    transition: 'color 200ms ease',
  };

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: transparent
          ? 'rgba(250, 247, 242, 0.85)'
          : 'var(--color-background-primary)',
        backdropFilter: 'saturate(1.2) blur(14px)',
        WebkitBackdropFilter: 'saturate(1.2) blur(14px)',
        borderBottom: '1px solid var(--color-border-light)',
      }}
    >
      <div
        style={{
          maxWidth: '1240px',
          margin: '0 auto',
          padding: '1.125rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '2rem',
        }}
      >
        {/* Logo */}
        <button
          onClick={() => navigate('home')}
          style={{
            all: 'unset',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'baseline',
            gap: '0.25rem',
          }}
          aria-label="Baza home"
        >
          <span
            style={{
              fontFamily: 'var(--font-family-display)',
              fontSize: '1.625rem',
              fontWeight: 400,
              fontStyle: 'italic',
              letterSpacing: '-0.02em',
              color: 'var(--color-text-primary)',
              lineHeight: 1,
            }}
          >
            baza
          </span>
          <span
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '999px',
              background: 'var(--color-accent-rosegold)',
              marginBottom: '3px',
            }}
          />
        </button>

        {/* Desktop nav */}
        <nav
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '1.75rem',
            marginLeft: '0.75rem',
          }}
          className="hide-on-mobile"
        >
          <button style={{ all: 'unset', ...linkStyle }} onClick={() => navigate('browse')}>
            Browse
          </button>
          <button style={{ all: 'unset', ...linkStyle }} onClick={() => navigate('request')}>
            Post a request
          </button>
          <button style={{ all: 'unset', ...linkStyle }} onClick={() => navigate('home')}>
            How it works
          </button>
          <button style={{ all: 'unset', ...linkStyle }} onClick={() => navigate('signup')}>
            For vendors
          </button>
        </nav>

        {/* Desktop search */}
        <div
          onClick={() => navigate('services')}
          className="hide-on-mobile"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            minWidth: '280px',
            padding: '0.625rem 1rem',
            background: 'var(--color-background-card)',
            border: '1px solid var(--color-border-medium)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            transition: 'border-color 200ms ease',
          }}
        >
          <Search size={16} strokeWidth={1.75} style={{ color: 'var(--color-text-muted)' }} />
          <span style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
            Find a service or vendor
          </span>
        </div>

        {/* Desktop auth */}
        <div className="hide-on-mobile" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            style={{ all: 'unset', ...linkStyle }}
            onClick={() => navigate('signin')}
          >
            Sign in
          </button>
          <button
            onClick={() => navigate('signup')}
            style={{
              all: 'unset',
              cursor: 'pointer',
              padding: '0.625rem 1.25rem',
              background: 'var(--color-accent-rosegold)',
              color: 'var(--color-text-inverse)',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: 'var(--radius-md)',
              transition: 'background 200ms ease',
            }}
          >
            Join
          </button>
        </div>

        {/* Mobile menu toggle */}
        <button
          className="show-on-mobile"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          style={{
            all: 'unset',
            cursor: 'pointer',
            padding: '0.5rem',
            marginLeft: 'auto',
            color: 'var(--color-text-primary)',
          }}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="show-on-mobile"
          style={{
            padding: '0.75rem 1.5rem 1.5rem',
            borderTop: '1px solid var(--color-border-light)',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            background: 'var(--color-background-primary)',
          }}
        >
          {[
            ['Browse', 'browse'],
            ['Post a request', 'request'],
            ['For vendors', 'signup'],
            ['Sign in', 'signin'],
          ].map(([label, name]) => (
            <button
              key={name}
              style={{ all: 'unset', ...linkStyle, fontSize: '1.0625rem' }}
              onClick={() => {
                navigate(name as any);
                setOpen(false);
              }}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => {
              navigate('signup');
              setOpen(false);
            }}
            style={{
              all: 'unset',
              cursor: 'pointer',
              padding: '0.75rem 1.25rem',
              background: 'var(--color-accent-rosegold)',
              color: 'var(--color-text-inverse)',
              fontSize: '0.9375rem',
              fontWeight: 500,
              borderRadius: 'var(--radius-md)',
              textAlign: 'center',
              marginTop: '0.5rem',
            }}
          >
            Join as a vendor
          </button>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .hide-on-mobile { display: none !important; }
        }
        @media (min-width: 901px) {
          .show-on-mobile { display: none !important; }
        }
      `}</style>
    </header>
  );
}
